/*----------------------------------
- LANDING PAGE STYLES (SSOT)
----------------------------------*/

/**
 * The type and surface vocabulary of the 2026-09 redesign, as utility strings.
 *
 * Every marketing page is built from the same few pieces: white cards on the cream
 * ground, tinted panels that group cards, dark ink blocks for summaries and final
 * CTAs, and one heading scale. They live here so a page cannot drift by a pixel from
 * its siblings, and so Tailwind emits each utility once instead of near-duplicates.
 * Phone values come first; `lg:` carries the 1440 mockup values.
 */

/*----------------------------------
- LAYOUT
----------------------------------*/

/** Page column: 20px gutters on a phone, the 1240px measure with 32px gutters on desktop. */
export const PAGE_CONTAINER_CLASS = 'mx-auto w-full max-w-[1240px] px-5 lg:px-8';

/**
 * The boards' column: 1500px of content, with the page column's gutters outside it (`box-content`,
 * so the measure is the content's). The agenda (`/agenda`: the month grid, the map and the list
 * side by side, founder decision of 2026-10-01), the TLD board (`/tld`) and the applications board
 * (`/tld/applications`), whose full column tier fits beside the rail at this width (founder
 * decision of 2026-10-03). Product's boards read the same 1500px (`BOARD_CONTENT_WIDTH_CLASS`).
 * The sections under a board keep `PAGE_CONTAINER_CLASS`.
 */
export const WIDE_PAGE_CONTAINER_CLASS = 'mx-auto box-content max-w-[1500px] px-5 lg:px-8';

/** Vertical rhythm between the sections of a page. The hero uses `HERO_SECTION_CLASS`. */
export const SECTION_SPACING_CLASS = 'pt-14 lg:pt-28';
export const HERO_SECTION_CLASS = 'pt-7 lg:pt-[72px]';
/** The last section before the footer closes the page with bottom space as well. */
export const LAST_SECTION_SPACING_CLASS = 'pb-0 pt-14 lg:pb-24 lg:pt-28';

/*----------------------------------
- TYPE
----------------------------------*/

export const PAGE_TITLE_CLASS =
    'text-pretty text-[38px] font-medium leading-[1.05] tracking-[-0.035em] text-[#1b1d1e] lg:text-[58px] lg:leading-[1.02] lg:tracking-[-0.04em]';
export const PAGE_LEAD_CLASS = 'text-pretty text-base leading-[1.6] text-[#66696b] lg:text-lg';

export const SECTION_TITLE_CLASS =
    'text-pretty text-[28px] font-medium leading-[1.1] tracking-[-0.03em] text-[#1b1d1e] lg:text-[40px] lg:leading-[1.08] lg:tracking-[-0.035em]';
export const SECTION_LEAD_CLASS = 'mt-3 text-[15px] leading-[1.6] text-[#66696b] lg:mt-4 lg:text-[17px]';

export const CARD_TITLE_CLASS = 'text-[17px] font-bold leading-[1.25] tracking-[-0.015em] text-[#1b1d1e] lg:text-[19px]';
export const CARD_BODY_CLASS = 'text-[14.5px] leading-[1.6] text-[#66696b]';

/** Small uppercase label above a group or inside a dark block. */
export const EYEBROW_CLASS = 'text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#6a695e]';

/*----------------------------------
- SURFACES
----------------------------------*/

export const WHITE_CARD_CLASS = 'rounded-[22px] border border-[#ebe7de] bg-white';
/** Groups a set of white cards. Holds them with a 12px inset on every side. */
export const TINTED_PANEL_CLASS = 'rounded-[28px] bg-[#f1ece6] p-2.5 lg:rounded-[32px] lg:p-3';
/** Flat ink block for summaries. The gradient variant is `.ud-dark-block` in shared.css. */
export const INK_BLOCK_CLASS = 'rounded-[28px] bg-[#141516] text-white lg:rounded-[32px]';
/** Number or letter badge that opens a card. */
export const CARD_BADGE_CLASS =
    'inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#f1ece6] text-[13px] font-extrabold text-[#1b1d1e]';

/*----------------------------------
- CONTROLS
----------------------------------*/

/** Secondary pill beside a `CtaPill`: same height, outlined on white. */
export const OUTLINE_PILL_CLASS =
    'inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[#c9c8c2] bg-white px-6 text-[15px] font-semibold text-[#1b1d1e] transition-colors hover:border-[#9a9d9e] lg:min-h-14 lg:text-base';

/** Segmented control track and its two option states (audience tabs, job tabs, doc switcher). */
export const SEGMENTED_TRACK_CLASS = 'inline-flex gap-1 rounded-full bg-[#edeae2] p-1';
export const SEGMENTED_OPTION_CLASS =
    'inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-full px-4 text-sm font-semibold text-[#4a4d4e] transition-colors lg:px-5';
export const SEGMENTED_OPTION_ACTIVE_CLASS = 'bg-[#1b1d1e] font-bold text-white';

/*----------------------------------
- PHONE CAROUSEL
----------------------------------*/

/** The phone swipe row both carousels share: edge bleed, card-by-card snap, no scrollbar. */
const PHONE_SNAP_ROW_CLASS =
    '-mx-5 flex snap-x snap-mandatory scroll-px-5 gap-2.5 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/**
 * A desktop grid that swipes on a phone. The row bleeds to the screen edges and snaps
 * card by card; from `lg` up the page supplies its own grid classes after this string.
 * Scrollbars are hidden because the peeking next card is the affordance.
 */
export const SNAP_ROW_CLASS = `${PHONE_SNAP_ROW_CLASS} lg:mx-0 lg:overflow-visible lg:px-0`;
export const SNAP_ITEM_CLASS = 'w-[300px] flex-none snap-start lg:w-auto';

/**
 * A row that swipes at every width, for a card set whose count does not fill the desktop
 * grid (seven cards leave one alone on a three-wide grid). From `lg` it stays inside the
 * page container at a fixed card width: three cards and part of a fourth show in the
 * 1240 px container, so the cut card says there is more. Used through `SnapCarousel`,
 * which adds the arrows a mouse needs.
 */
// `py-1`: a horizontal scroller clips vertically too, which would cut the 2 px hover lift.
export const CAROUSEL_ROW_CLASS = `${PHONE_SNAP_ROW_CLASS} py-1 lg:mx-0 lg:scroll-px-0 lg:gap-3 lg:px-0`;
export const CAROUSEL_ITEM_CLASS = 'w-[260px] flex-none snap-start lg:w-[344px]';
